import type { HttpBindings } from "@hono/node-server";
import { Hono } from "hono";
import type { UpgradeWebSocket } from "hono/ws";
import type { WebSocket } from "ws";
import { getLogger } from "../logger";
import { authMiddleware } from "./middleware/auth.middleware";
import { corsMiddleware } from "./middleware/cors.middleware";
import { errorMiddleware } from "./middleware/error.middleware";
import { createBrowseRoutes } from "./routes/browse.routes";
import { createConversationRoutes } from "./routes/conversations.routes";
import { createHealthRoutes } from "./routes/health.routes";
import { createMiscRoutes } from "./routes/misc.routes";
import { createPairRoutes } from "./routes/pair.routes";
import { createProgressRoutes } from "./routes/progress.routes";
import { createProjectRoutes } from "./routes/projects.routes";
import { createScannerRoutes } from "./routes/scanner.routes";
import { createSessionRoutes } from "./routes/sessions.routes";
import { createWsRoutes } from "./routes/ws.routes";
import type { ApiDeps } from "./types/api-deps";

export type AppEnv = {
  Bindings: HttpBindings;
  Variables: {
    requestId?: string;
    validatedBody?: unknown;
    validatedQuery?: unknown;
  };
};

export const createHonoApp = (deps: ApiDeps, upgradeWebSocket?: UpgradeWebSocket<WebSocket>) => {
  const app = new Hono<AppEnv>();
  const httpLog = getLogger("http");

  app.use("*", async (c, next) => {
    const start = Date.now();
    const ua = c.req.header("user-agent") ?? "";
    await next();
    if (!deps.logMenubarRequests && c.req.header("x-client") === "menubar") return;
    const ms = Date.now() - start;
    httpLog.info(`[req] ${c.req.method} ${c.req.path} → ${c.res.status} ${ms}ms`, {
      method: c.req.method,
      path: c.req.path,
      status: c.res.status,
      ms,
      ua,
      event: "http.request",
    });
  });
  app.use("*", corsMiddleware());
  app.use("*", authMiddleware(deps));
  app.onError(errorMiddleware);

  app.route("/healthz", createHealthRoutes());
  app.route("/", createMiscRoutes(deps));
  app.route("/api/sessions", createSessionRoutes(deps));
  app.route("/api/conversations", createConversationRoutes(deps));
  app.route("/api/projects", createProjectRoutes(deps));
  app.route("/api/pair", createPairRoutes(deps));
  app.route("/api", createBrowseRoutes(deps));
  app.route("/", createScannerRoutes(deps));
  app.route("/internal", createProgressRoutes(deps));

  if (upgradeWebSocket) {
    app.route("/", createWsRoutes(deps, upgradeWebSocket));
  }

  return app;
};
